import {
  usersCollection,
  professionalsGeoCollection,
  specialtiesCollection,
  postsCollection,
  adminUsersCollection,
  premiumUsersCollection,
  favoritesCollection,
  plansCollection
} from '../firebaseConfig'

import axios from 'axios'
import { HERE_API } from '../app.config'

const DEBUG = process.env.NODE_ENV === 'development'

export const actions = {
  clearData ({ commit }) {
    commit('setCurrentUser', null)
    commit('setUserProfile', {})
    commit('setPosts', null)
    commit('setProfessionalProfile', {})
    commit('setUsers', [])
    commit('setAdmin', false)
    commit('setCurrentUserFavorites', [])
  },
  async fetchUserProfile ({ commit, state }) {
    try {
      const res = await usersCollection.doc(state.currentUser.uid).get()
      commit('setUserProfile', res.data())
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async fetchProfessionalProfile ({ commit, state }) {
    try {
      const res = await professionalsGeoCollection
        .doc(state.currentUser.uid)
        .get()

      commit('setProfessionalProfile', res.data())
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async fetchSpecialties ({ commit }) {
    try {
      const docs = await specialtiesCollection.get()

      docs.forEach(specialty => {
        commit('setSpecialties', specialty.data())
      })
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async fetchUsers ({ commit }, filters) {
    const { name, lastName, email, userType, isPremium } = filters

    const users = []

    let query = usersCollection

    if (name) query = query.where('name', '==', name)
    if (lastName) query = query.where('lastName', '==', lastName)
    if (email) query = query.where('email', '==', email)
    if (userType !== 'all') query = query.where('userType', '==', userType)
    if (isPremium !== 'all') query = query.where('isPremium', '==', isPremium)

    try {
      const result = await query
        .limit(100)
        .get()

      if (!result.empty && result.docs) {
        result
          .docs
          .forEach(doc => {
            users.push({
              ...doc.data(),
              id: doc.id
            })
          })
      }

      commit('setUsers', users)
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async fetchPosts ({ commit }, filters) {
    const { title, authorName, authorLastName, createdOn, isPublished } = filters

    const posts = []

    let query = postsCollection

    if (title) query = query.where('title', '==', title)
    if (authorName) query = query.where('author.name', '==', authorName)
    if (authorLastName) query = query.where('author.lastName', '==', authorLastName)
    if (createdOn) query = query.where('createdOn', '==', createdOn)
    if (isPublished !== 'all') query = query.where('isPublished', '==', isPublished)

    try {
      const result = await query
        .limit(100)
        .get()

      if (!result.empty && result.docs) {
        result
          .docs
          .forEach(doc => {
            posts.push({
              ...doc.data(),
              id: doc.id
            })
          })
      }

      commit('setPosts', posts)
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async fetchFavoriteProfessionals ({ commit, state }) {
    const userId = state.currentUser.uid

    try {
      const query = await favoritesCollection
        .doc(userId)
        .collection('user_favorites')
        .get()

      const favorites = []

      if (!query.empty) {
        query.forEach(doc => {
          favorites.push(doc.data())
        })
      }

      commit('setCurrentUserFavorites', favorites)
    } catch (error) {
      DEBUG && console.log(error)
    }
  },
  async addProfessionalToFavorites ({ commit, state }, favoriteProfessionalData) {
    try {
      if (!favoriteProfessionalData) {
        throw new Error('No professional data found to add to favorites')
      }

      if (!(state.currentUser && state.currentUser.uid)) {
        throw new Error('User is not logged in')
      }

      const { userId: professionalId } = favoriteProfessionalData
      const uid = state.currentUser.uid

      await favoritesCollection
        .doc(uid)
        .collection('user_favorites')
        .doc(professionalId)
        .set(favoriteProfessionalData)

      commit('addToCurrentUserFavorites', favoriteProfessionalData)
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async removeProfessionalFromFavorites ({ commit, state }, professionalId) {
    try {
      if (!(state.currentUser && state.currentUser.uid)) {
        throw new Error('User is not logged in')
      }

      if (!professionalId) {
        throw new Error('No professional id found')
      }

      const uid = state.currentUser.uid

      await favoritesCollection
        .doc(uid)
        .collection('user_favorites')
        .doc(professionalId)
        .delete()

      commit('removeFromCurrentUserFavorites', professionalId)
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async getCoordinatesFromLocationName ({ commit }, locationName) {
    const BASE_URL = 'https://geocoder.api.here.com/6.2/geocode.json'
    const { APP_ID, APP_CODE } = HERE_API

    const URL = `${BASE_URL}?app_id=` +
      `${APP_ID}&app_code=` +
      `${APP_CODE}&searchtext=` +
      `${locationName}`

    try {
      const { data } = await axios.get(URL)
      const View = data.Response.View
      const responseIsInvalid = !View || View.length === 0

      if (responseIsInvalid) {
        throw new Error('Informações de Endereço Inválidas')
      }

      const coordinates = await View[0].Result[0].Location.DisplayPosition

      commit('setCoordinates', coordinates)
    } catch (error) {
      DEBUG && console.error(error)
    }
  },

  async getLocationNameFromCoordinates ({ commit }, coordinates) {
    const BASE_URL = 'https://reverse.geocoder.api.here.com/6.2/reversegeocode.json'
    const { APP_ID, APP_CODE } = HERE_API
    const { lat, lng } = coordinates

    const URL = `${BASE_URL}?app_id=` +
      `${APP_ID}&app_code=` +
      `${APP_CODE}&prox=` +
      `${lat},${lng}&mode=retrieveAreas`

    try {
      const { data } = await axios.get(URL)
      const View = data.Response.View
      const responseIsInvalid = !View || View.length === 0

      if (responseIsInvalid) {
        throw new Error('Informações de Endereço Inválidas')
      }

      const Address = View[0].Result[0].Location.Address

      const { City, State } = Address

      const Country = Address.AdditionalData.find(data => data.key === 'CountryName').value

      commit('setLocationName', `${City}, ${State}, ${Country}`)
    } catch (error) {
      DEBUG && console.error(error)
    }
  },

  async setPaymentProcessing ({ state }, status) {
    try {
      await usersCollection
        .doc(state.currentUser.uid)
        .update({ isProcessingPayment: status })
    } catch (error) {
      DEBUG && console.error(error)
    }
  },

  updateProfile ({ state }, data) {
    const { name, lastName, email, profilePictureUrl } = data
    usersCollection
      .doc(state.currentUser.uid)
      .update({ name, lastName, email, profilePictureUrl })
      .then(user => {
        // update all posts by user to reflect new name
        postsCollection
          .where('userId', '==', state.currentUser.uid)
          .get()
          .then(docs => {
            docs.forEach(doc => {
              postsCollection.doc(doc.id).update('author', {
                name,
                lastName,
                picture: profilePictureUrl
              })
            })
          })
      }).catch(err => {
        DEBUG && console.error(err)
      })
  },
  setActiveDashboardSection ({ commit }, name) {
    commit('setActiveDashboardSection', name)
  },
  async checkAdminStatus ({ commit, state }) {
    try {
      const doc = await adminUsersCollection
        .doc(state.currentUser.uid)
        .get()

      const status = doc.exists

      commit('setAdmin', status)
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async fetchPremiumStatus ({ commit }, userId) {
    try {
      const doc = await premiumUsersCollection.doc(userId).get()

      if (doc.exists) {
        const data = doc.data()
        // eslint-disable-next-line prefer-const
        let plan = {}

        data.premiumSince && (plan.activation = data.premiumSince.toDate())
        data.premiumUntil && (plan.expiration = data.premiumUntil.toDate())
        data.isRecurring && (plan.isRecurring = data.isRecurring)

        commit('setPlan', plan)
        commit('setPremium', true)
      } else {
        commit('setPlan', {})
        commit('setPremium', false)
      }
    } catch (error) {
      DEBUG && console.error(error)
    }
  },
  async fetchPlans ({ commit }) {
    try {
      const plans = (await plansCollection.get()).docs.map(doc => doc.data())

      if (plans) {
        plans.forEach((plan, i) => {
          plans[i].amountPerPayment = parseFloat(plan.amountPerPayment)
            .toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            })
        })
      }

      commit('setPlans', plans)
    } catch (error) {
      console.error(error)
    }
  },
  async getPremiumStatusFromUser (_, id) {
    try {
      const doc = await usersCollection.doc(id).get()
      if (!doc.exists) {
        throw new Error('User not found')
      }
      return doc.data().isPremium
    } catch (error) {
      DEBUG && console.error(error)
      return null
    }
  }
}

export default actions
